import { useEffect, useState } from "react";
import { CircularProgress, useTheme, Button, Typography } from "@mui/material";
import { useMediaQuery } from "react-responsive";
import { fetchListUserMessage, fetchShowUserMessage } from "@/api/user/home";
import TableWallet from "../walletReports/table";
import AlertDialog from "@/shared/components/kit/modal";
import StackKit from "@/shared/components/kit/stack";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";
import { toPersianNumber } from "@/hooks/useThousandsSeparator";
import moment from "jalali-moment";
import BoxLayoutContent from "@/shared/components/layout/boxLauotContent";
import CardBoxLayout from "../../layout/cardBoxLayout";
import ContentSkeleton from "../walletReports/content.skeleton";
import SpinnerKit from "@/shared/components/kit/spinner";

const TypoGraphyKit = ({ children, variant, sx }: any) => (
    <Typography variant={variant} sx={sx}>
      {children}
    </Typography>
);

interface Message {
  id: number;
  title: string;
  message: string;
  at_created: number;
  read?: number;
}

export default function UserMessagesTable() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingShowMessage, setLoadingShowMessage] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [loadingMessageId, setLoadingMessageId] = useState<number | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalMessages, setTotalMessages] = useState(0);

  const theme = useTheme();

  const loadMessages = async () => {
    setLoading(true);
    try {
      const res = await fetchListUserMessage(currentPage, rowsPerPage);
      setMessages(res.ListUserMessage.messages);
      setTotalMessages(res.ListUserMessage.total);
    } catch (err) {
      console.error("Error fetching messages:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
  }, [currentPage, rowsPerPage]);

  const handleShowMessage = async (id: number) => {
    setLoadingMessageId(id);
    try {
      const res = await fetchShowUserMessage(id);
      setSelectedMessage(res.showUserMessage);
      setDialogOpen(true);
    } catch (err) {
      console.error("Error fetching message details:", err);
    } finally {
      setLoadingMessageId(null);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedMessage(null);
    setLoadingMessageId(null);
    loadMessages();
  };

  const tableHeaderConfig = [
    { id: "id", title: "شناسه" },
    { id: "title", title: "عنوان" },
    { id: "read", title: "وضعیت خوانده شده" },
    { id: "at_created", title: "زمان ایجاد" },
    { id: "action", title: "عملیات" },
  ];

  const tableBodyData = messages.map((msg) => ({
    ...msg,
    read: (
        <div className={msg.read === 1 ? "text-green-600 bg-green-600/10 p-1 rounded-lg" : "text-red-600 bg-red-600/10 p-1 rounded-lg"}>
          {msg.read === 1 ? "خوانده شده" : "خوانده نشده"}
        </div>
    ),
    at_created: toPersianNumber(
        moment(msg.at_created).locale("fa").format("HH:MM"),
    ),
    action: (
        <Button variant="text" color={'info'} size="small" onClick={() => handleShowMessage(msg.id)} sx={{ minWidth: "80px" }} disabled={loadingMessageId === msg.id}>
          {loadingMessageId === msg.id ?
              <CircularProgress size={20} sx={{ color: "#1976d2" }} /> :
              "مشاهده"
          }
        </Button>
    ),
  }));

  const paginationProps = {current_page: currentPage, per_page: rowsPerPage, total: totalMessages};

  const handlePageChangeCallback = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChangeCallback = (newSize: number) => {
    setRowsPerPage(newSize);
    setCurrentPage(1);
  };

  const isXs = useMediaQuery({ maxWidth: 599 });
  const isSm = useMediaQuery({ minWidth: 600, maxWidth: 959 });

  return (
      <CardBoxLayout sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <BoxLayoutContent
            titleElement={
              <StackKit direction={"row"} flexWrap="wrap" alignItems="center" gap={1}>
                <TypoGraphyKit variant="h6">
                  <MessageOutlinedIcon />
                </TypoGraphyKit>
                <TypoGraphyKit variant="header" sx={{ color: "#082A2E" }}>
                  مدیریت پیام‌های ادمین
                </TypoGraphyKit>
              </StackKit>
            }
        >
          {loading && !dialogOpen ? (
              <ContentSkeleton />
          ) : (
              <TableWallet
                  dataHeader={tableHeaderConfig}
                  dataTbody={tableBodyData}
                  pagination={paginationProps}
                  onPageChange={handlePageChangeCallback}
                  onItemsPerPageChange={handleItemsPerPageChangeCallback}
              />
          )}

          {dialogOpen && (
              <AlertDialog
                  open={dialogOpen}
                  onClose={handleCloseDialog}
                  heightMain={isXs ? "500px" : isSm ? "660px" : "660px"}
                  header={
                    <StackKit direction="row" sx={{ width: "100%" }} justifyContent="center" gap={5}>
                      <VpnKeyOutlinedIcon sx={{ color: "#fff" }} />
                      <TypoGraphyKit variant="h6">{"جزئیات پیام"} </TypoGraphyKit>
                    </StackKit>
                  }
                  content={
                    loadingMessageId ? (
                        <StackKit sx={{ height: "100%", width: "100%" }} justifyContent="center" alignItems="center">
                          <CircularProgress sx={{ color: "#fff" }} />
                        </StackKit>
                    ) : (
                        selectedMessage?.message || "متنی یافت نشد."
                    )
                  }
                  paragraph={selectedMessage?.title || ""}
                  agreeText="بستن"
              />
          )}
        </BoxLayoutContent>
      </CardBoxLayout>
  );
}